'use client';

import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { ChatMessage, TaskFile } from '@/entities/interview-task';
import {
	type AIAttachmentV1,
	type AIDialogCurrentTaskV1,
	AIDialogStatusV1,
	type AIVisibleMessageV1,
	type GetAIDialogResponseV1,
} from '@/shared/api';

import type { ReportDialogResult } from '../actions/report-dialog-action';
import type { SendMessageResult } from '../actions/send-message-action';
import type { StopDialogResult } from '../actions/stop-dialog-action';
import { canSendInStatus, useAiDialogStore } from './ai-dialog-store';

const POLL_INTERVAL_MS = 4000;

export type UseAiDialogResult = {
	dialog: GetAIDialogResponseV1 | null;
	currentTask: AIDialogCurrentTaskV1 | null;
	messages: ChatMessage[];
	isThinking: boolean;
	error: string | null;
	/** Можно ли отправлять сообщение прямо сейчас (status === idle | aiError). */
	canSend: boolean;
	sendMessage: (
		text: string,
		attachFiles?: TaskFile[],
	) => Promise<SendMessageResult>;
	completeStaticTask: (taskId: string) => Promise<void>;
	completeSelectTask: (taskId: string, optionId: string) => Promise<void>;
	submitCode: (problemId: string, files: AIAttachmentV1[]) => Promise<void>;
	stopDialog: () => Promise<StopDialogResult>;
	reportDialog: () => Promise<ReportDialogResult>;
};

type UseAiDialogArgs = {
	dialogId: string;
	initialDialog: GetAIDialogResponseV1;
	initialMessages: AIVisibleMessageV1[];
};

/**
 * Тонкая обёртка над useAiDialogStore.
 * На mount синхронизирует store с серверными initial-данными, запускает polling
 * через `listMessagesAction` / `getDialogAction`. Все мутации идут через store.
 */
export function useAiDialog({
	dialogId,
	initialDialog,
	initialMessages,
}: UseAiDialogArgs): UseAiDialogResult {
	const initialize = useAiDialogStore((s) => s.initialize);
	const reset = useAiDialogStore((s) => s.reset);
	const refreshDialog = useAiDialogStore((s) => s.refreshDialog);
	const loadMessages = useAiDialogStore((s) => s.loadMessages);

	const state = useAiDialogStore(
		useShallow((s) => ({
			dialog: s.dialog,
			messages: s.messages,
			isThinking: s.isThinking,
			pendingCount: s.pending.length,
			error: s.error,
		})),
	);

	const actions = useAiDialogStore(
		useShallow((s) => ({
			sendMessage: s.sendMessage,
			completeStaticTask: s.completeStaticTask,
			completeSelectTask: s.completeSelectTask,
			submitCode: s.submitCode,
			stopDialog: s.stopDialog,
			reportDialog: s.reportDialog,
		})),
	);

	// Sync store with SSR-данными при смене dialogId.
	useEffect(() => {
		initialize({ dialogId, initialDialog, initialMessages });
		return () => reset();
	}, [dialogId, initialDialog, initialMessages, initialize, reset]);

	// Polling.
	useEffect(() => {
		const status = state.dialog?.status;
		const tick = async () => {
			await loadMessages();
			if (
				status === AIDialogStatusV1.AiGenerating ||
				status === AIDialogStatusV1.AiCompaction ||
				status === AIDialogStatusV1.AiReport ||
				status === AIDialogStatusV1.Idle
			) {
				await refreshDialog();
			}
		};
		const id = setInterval(tick, POLL_INTERVAL_MS);
		return () => clearInterval(id);
	}, [state.dialog?.status, loadMessages, refreshDialog]);

	return {
		dialog: state.dialog,
		currentTask: state.dialog?.currentTask ?? null,
		messages: state.messages,
		// Пока в очереди есть нераскрытые сообщения — держим индикатор «AI печатает»
		// между ними, чтобы появление выглядело поочерёдным.
		isThinking: state.isThinking || state.pendingCount > 0,
		error: state.error,
		canSend: canSendInStatus(state.dialog?.status),
		...actions,
	};
}
